import { toast } from 'react-toastify';
import * as yup from 'yup';
import { nullableString } from '@/utils/validations';
import type { AccommodationPreference, CreateClientFormData } from '@/types/clientsApiTypes';
import {
  DRINKING_PREFERENCE_OPTIONS,
  INTEREST_PREFERENCE_OPTIONS,
  PILLOW_PREFERENCE_OPTIONS,
  ROOM_PREFERENCE_OPTIONS,
  SPA_PREFERENCE_OPTIONS,
} from '@/types/clientsApiTypes';
import {
  ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH,
  combineStayAccommodationRawFromPreferenceStay,
  flattenStayAccommodationTypesForForm,
  isKnownAccommodationPreferenceKey,
} from '@/utils/preferences/partitionAccommodationTypes';
import { useUpdateClientPreferencesMutation } from '../../../hooks/useUpdateClientPreferencesMutation';
import { ModalKeys } from '../../EditSectionModals/types';
import { defineSection } from '../../EditSectionModals/utils/defineSection';
import { EditTravelPreferencesByCategoryForm } from './EditTravelPreferencesByCategoryForm';
import { MAX_FLIGHT_PREFERENCES_LENGTH } from './const';
import type { TravelPreferencesByCategoryPayload } from './types';

const maxCharsMsg = (sectionLabel: string) =>
  `${sectionLabel} must not exceed ${MAX_FLIGHT_PREFERENCES_LENGTH} characters`;

const schema = yup.object({
  travel: nullableString().defined().max(
    MAX_FLIGHT_PREFERENCES_LENGTH,
    maxCharsMsg('Travel preferences')
  ),
  stay: yup
    .object()
    .shape({
      accommodation_types: yup
        .array()
        .of(
          yup
            .string()
            .required()
            .test(
              'accommodation-standard-or-legacy',
              `Each entry must be a standard accommodation option or legacy text (max ${ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH} characters)`,
              (value) => {
                if (value == null || value === '') return false;
                if (isKnownAccommodationPreferenceKey(value)) return true;
                return value.length <= ACCOMMODATION_LEGACY_ENTRY_MAX_LENGTH;
              }
            )
        )
        .nullable()
        .default([]),
      preferences_note: nullableString().defined().max(
        MAX_FLIGHT_PREFERENCES_LENGTH,
        maxCharsMsg('Stay preferences')
      ),
    })
    .required(),
  general_interests: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(INTEREST_PREFERENCE_OPTIONS.map((o) => o.key))
        .required()
    )
    .nullable()
    .default([]),
  beverage_preferences: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(DRINKING_PREFERENCE_OPTIONS.map((o) => o.key))
        .required()
    )
    .nullable()
    .default([]),
  pillow_preferences: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(PILLOW_PREFERENCE_OPTIONS.map((o) => o.key))
        .required()
    )
    .nullable()
    .default([]),
  room_configuration_preferences: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(ROOM_PREFERENCE_OPTIONS.map((o) => o.key))
        .required()
    )
    .nullable()
    .default([]),
  spa_preferences: yup
    .array()
    .of(
      yup
        .string()
        .oneOf(SPA_PREFERENCE_OPTIONS.map((o) => o.key))
        .required()
    )
    .nullable()
    .default([]),
  spa_preferences_special_requests: nullableString().defined().max(
    MAX_FLIGHT_PREFERENCES_LENGTH,
    maxCharsMsg('Spa preferences')
  ),
  likes: nullableString().defined().max(
    MAX_FLIGHT_PREFERENCES_LENGTH,
    maxCharsMsg('Likes & Dislikes')
  ),
  dislikes: nullableString().defined(),
  personal: yup
    .object()
    .shape({
      personal_metric_system: yup.string().oneOf(['metric', 'us']).required().default('us'),
      information: nullableString().defined().max(
        MAX_FLIGHT_PREFERENCES_LENGTH,
        maxCharsMsg('Personal information')
      ),
      comfort_related: yup
        .object()
        .shape({
          us: yup
            .object()
            .shape({
              height: nullableString().defined(),
              weight: nullableString().defined(),
            })
            .required()
            .default({ height: null, weight: null }),
          metric: yup
            .object()
            .shape({
              height: nullableString().defined(),
              weight: nullableString().defined(),
            })
            .required()
            .default({ height: null, weight: null }),
        })
        .required(),
    })
    .required(),
});

function useTravelPreferencesByCategoryOnSave() {
  const mutation = useUpdateClientPreferencesMutation();

  return async (payload: TravelPreferencesByCategoryPayload) => {
    await mutation.mutateAsync(payload.preference_data, {
      onSuccess: () => {
        toast.success('Preferences updated successfully');
      },
    });
  };
}

function mapClientToValues(client: CreateClientFormData): yup.InferType<typeof schema> {
  const pd = client?.primary_passenger?.preference_data;
  const stayRawForFlatten =
    combineStayAccommodationRawFromPreferenceStay(pd?.stay) ??
    (Array.isArray(pd?.stay?.accommodation_types) ? pd.stay.accommodation_types : []);
  return {
    travel: pd?.travel ?? null,
    stay: {
      accommodation_types: flattenStayAccommodationTypesForForm(
        stayRawForFlatten
      ) as yup.InferType<typeof schema>['stay']['accommodation_types'],
      preferences_note: pd?.stay?.preferences_note ?? null,
    },
    general_interests: (pd?.general_interests ?? []) as yup.InferType<typeof schema>['general_interests'],
    beverage_preferences: (pd?.beverage_preferences ?? []) as yup.InferType<typeof schema>['beverage_preferences'],
    pillow_preferences: (pd?.pillow_preferences ?? []) as yup.InferType<typeof schema>['pillow_preferences'],
    room_configuration_preferences: (pd?.room_configuration_preferences ?? []) as yup.InferType<
      typeof schema
    >['room_configuration_preferences'],
    spa_preferences: (pd?.spa_preferences ?? []) as yup.InferType<typeof schema>['spa_preferences'],
    spa_preferences_special_requests: pd?.spa_preferences_special_requests ?? null,
    likes: pd?.likes ?? null,
    dislikes: pd?.dislikes ?? null,
    personal: {
      personal_metric_system: pd?.personal?.personal_metric_system ?? 'us',
      information: pd?.personal?.information ?? null,
      comfort_related: {
        us: {
          height: pd?.personal?.comfort_related?.us?.height ?? null,
          weight: pd?.personal?.comfort_related?.us?.weight ?? null,
        },
        metric: {
          height: pd?.personal?.comfort_related?.metric?.height ?? null,
          weight: pd?.personal?.comfort_related?.metric?.weight ?? null,
        },
      },
    },
  };
}

function mapValuesToPayload(values: yup.InferType<typeof schema>): TravelPreferencesByCategoryPayload {
  const trimmed = (s: string | null | undefined) => s?.trim() || undefined;

  return {
    preference_data: {
      travel: trimmed(values.travel),
      stay: {
        accommodation_types: (values.stay?.accommodation_types ?? []) as (AccommodationPreference | string)[],
        preferences_note: trimmed(values.stay?.preferences_note),
      },
      general_interests: values.general_interests ?? [],
      beverage_preferences: values.beverage_preferences ?? [],
      pillow_preferences: values.pillow_preferences ?? [],
      room_configuration_preferences: values.room_configuration_preferences ?? [],
      spa_preferences: values.spa_preferences ?? [],
      spa_preferences_special_requests: trimmed(values.spa_preferences_special_requests),
      likes: trimmed(values.likes),
      dislikes: trimmed(values.dislikes),
      personal: {
        information: trimmed(values.personal?.information),
        comfort_related: {
          us: {
            height: values.personal?.comfort_related?.us?.height ?? undefined,
            weight: values.personal?.comfort_related?.us?.weight ?? undefined,
          },
          metric: {
            height: values.personal?.comfort_related?.metric?.height ?? undefined,
            weight: values.personal?.comfort_related?.metric?.weight ?? undefined,
          },
        },
      },
    },
  };
}

export const travelPreferencesByCategorySection = defineSection<
  (typeof ModalKeys)['travel_preferences_by_category'],
  CreateClientFormData,
  typeof schema,
  TravelPreferencesByCategoryPayload
>({
  key: ModalKeys.travel_preferences_by_category,
  title: 'Travel preferences by category',
  subtitle: 'This information helps us plan a more comfortable travel experience.',
  schema,
  mapClientToValues,
  mapValuesToPayload,
  useOnSave: useTravelPreferencesByCategoryOnSave,
  Fields: EditTravelPreferencesByCategoryForm,
});